import { API_BASE_URL } from '../config';
import api from './axiosInstance';
import qs from 'qs';

export const fetchAllSystemUsers = async () => {
  try {
    // Llama al endpoint que trae a TODOS los usuarios
    const response = await api.get(`${API_BASE_URL}/Users/All`);

    return response.data.map((user) => ({
      label: user.name, // O user.name, o como se llame la propiedad
      value: user.id.toString(),
      backendId: user.id,
      roleId: user.roleId,
      active: user.active,
    }));
  } catch (error) {
    console.error(
      'Error fetching all system users:',
      error.response?.data || error.message
    );
    throw error;
  }
};

export const getUserCases = async (userId) => {
  try {
    const params = new URLSearchParams();
    params.append('userId', userId);

    const response = await api.get(
      `${API_BASE_URL}/Task/GetUserCases?${params}`
    );
    // Convertimos todos los casos al formato necesario para el dropdown
    return response.data.map((caso) => ({
      value: caso.caseId,
      label: caso.caseTitle || `Causa #${caso.id}`, // Asumiendo que el caso tiene un título o usando el ID como fallback
      backendId: caso.caseId,
      searchText:
        `${caso.caseId} ${caso.caseTitle || ''} ${caso.caseCode || ''}`.toLowerCase(), // Texto para búsqueda
    }));
  } catch (error) {
    console.error(
      'Error fetching user cases:',
      error.response?.data || error.message
    );
    throw error;
  }
};

export const fetchUsersForCase = async (caseId) => {
  if (!caseId) {
    return [];
  }
  try {
    const response = await api.get(
      `${API_BASE_URL}/Task/GetUsersForCase/${caseId}`
    );
    return response.data.map((user) => ({
      label: user.name, // O user.name
      value: user.id.toString(),
      backendId: user.id,
    }));
  } catch (error) {
    console.error(
      `Error fetching users for case ${caseId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

export const fetchTaskStates = async () => {
  try {
    const response = await api.get(`${API_BASE_URL}/Task/TaskStatus`);
    return response.data.map((state) => ({
      label: state.description,
      value: state.id.toString(),
      backendId: state.id,
    }));
  } catch (error) {
    console.error(
      'Error fetching task states:',
      error.response?.data || error.message
    );
    throw error;
  }
};

export const fetchTaskPriorities = async () => {
  try {
    const response = await api.get(`${API_BASE_URL}/Task/GetPriorities`);
    return response.data.map((priority) => ({
      label: priority.name,
      value: priority.id.toString(),
      backendId: priority.id,
    }));
  } catch (error) {
    console.error(
      'Error fetching task priorities:',
      error.response?.data || error.message
    );
    throw error;
  }
};

export const fetchTaskTitles = async () => {
  try {
    const response = await api.get(`${API_BASE_URL}/Task/GetTaskCode`); // Mapea el DTO { id, name } al formato { label, value, backendId }
    return response.data.map((title) => ({
      label: title.name,
      value: title.id,
      serviceId: title.serviceId,
      backendId: title.id,
    }));
  } catch (error) {
    console.error(
      'Error fetching task titles:',
      error.response?.data || error.message
    );
    throw error;
  }
};

export const fetchPagedTasks = async (queryParams) => {
  try {
    const params = {
      FiltroGlobal: queryParams.filtroGlobal || null,
      StatusId: queryParams.statusId ?? null,
      CasoId: queryParams.casoId ?? null,
      UsuarioAsignadoId: queryParams.usuarioAsignadoId ?? null,
      FechaDesde: queryParams.fechaDesde || null,
      FechaHasta: queryParams.fechaHasta || null,
      FechaVencimiento: queryParams.fechaVencimiento || null,
      FechaVencimientoDesde: queryParams.fechaVencimientoDesde || null,
      FechaVencimientoHasta: queryParams.fechaVencimientoHasta || null,
      PageNumber: queryParams.pageNumber || 1,
      PageSize: queryParams.pageSize || 10,
      SortBy: queryParams.sortBy || 'createdat',
      SortDirection: queryParams.sortDirection || 'DESC',
      CourtId: queryParams.courtId ?? null,
      BusinessAreaIds: queryParams.groups ?? null,
      PriorityId: queryParams.priorityId ?? null,
      HasDocument: queryParams.hasDocument ?? null,
      HasComments: queryParams.hasComments ?? null,
    };

    const response = await api.get(`${API_BASE_URL}/Task/PagedList`, {
      params,
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: 'repeat' }),
    });

    return response.data;
  } catch (error) {
    console.error(
      'Error fetching paged tasks:',
      error.response?.data || error.message
    );
    throw error;
  }
};

export const fetchCalendarTasks = async (queryParams) => {
  try {
    const params = {
      UsuarioAsignadoId: queryParams.usuarioAsignadoId ?? null,
      StatusId: queryParams.statusId ?? null,
      FechaDesde: queryParams.fechaDesde || null,
      FechaHasta: queryParams.fechaHasta || null,
    };

    const response = await api.get(`${API_BASE_URL}/Task/getCalendarTasks`, {
      params,
    });

    return response.data || [];
  } catch (error) {
    if (error.response?.status === 404) {
      return [];
    }
    console.error(
      'Error fetching calendar tasks:',
      error.response?.data || error.message
    );
    throw error;
  }
};

export const fetchTaskCounts = async (userId) => {
  if (!userId) {
    console.error('UserId is required for fetching task counts');
    throw new Error('UserId is required');
  }
  try {
    const response = await api.get(
      `${API_BASE_URL}/Task/CountsByStatus/${userId}`
    );

    return response.data;
  } catch (error) {
    console.error(
      'Error fetching task counts:',
      error.response?.data || error.message
    );
    throw error;
  }
};

export const fetchTaskDetailsById = async (taskId) => {
  if (!taskId) {
    console.error('fetchTaskDetailsById requires an id');
    throw new Error('Task id is required');
  }
  try {
    const response = await api.get(
      `${API_BASE_URL}/Task/GetTaskById/${taskId}`
    );
    return response.data;
  } catch (error) {
    console.error(
      `Error fetching task details for id=${taskId}`,
      error.response?.data || error.message
    );
    throw error;
  }
};

export const getAllTaskByIds = async (ids, isOld) => {
  if (!ids || ids.length === 0) return;

  try {
    const response = await api.post(`${API_BASE_URL}/Task/GetAllTaskByIds`, {
      ids,
      isOld,
    });
    return response.data;
  } catch (error) {
    console.error(
      `Error fetching task details for ids=${ids}`,
      error.response?.data || error.message
    );
    throw error;
  }
};

export const createTask = async (taskData) => {
  const toIntOrNull = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const normalizedTaskData = {
    ...taskData,
    statusId: toIntOrNull(taskData?.statusId),
    priorityId: toIntOrNull(taskData?.priorityId),
    assignedToId: toIntOrNull(taskData?.assignedToId),
    creatorId: toIntOrNull(taskData?.creatorId),
    caseId: toIntOrNull(taskData?.caseId),
    taskConfiguratorId: toIntOrNull(taskData?.taskConfiguratorId),
    templateId: toIntOrNull(taskData?.templateId),
  };

  try {
    const response = await api.post(`${API_BASE_URL}/Task/Add`, normalizedTaskData);
    return response.data;
  } catch (error) {
    console.error(
      'Error creating task:',
      error.response?.data || error.message
    );
    throw error;
  }
};

export const updateTask = async (taskId, taskData) => {
  if (!taskId) {
    console.error('updateTask requires an id');
    throw new Error('Task id is required');
  }
  try {
    const response = await api.put(
      `${API_BASE_URL}/Task/UpdateTask/${taskId}`,
      taskData
    );
    return response.data;
  } catch (error) {
    console.error(
      `Error updating task id=${taskId}`,
      error.response?.data || error.message
    );
    throw error;
  }
};

export const changeTaskInfo = async (
  taskId,
  statusId,
  updaterId,
  expirationDate = null,
  assignedUserId = null,
  tasksDoc = {}
) => {
  try {
    const params = new URLSearchParams();
    params.append('taskId', taskId);
    params.append('statusId', statusId);
    params.append('updaterId', updaterId);
    params.append('expirationDate', expirationDate);
    params.append('assignedUserId', assignedUserId);

    const response = await api.put(
      `${API_BASE_URL}/Task/ChangeTaskInfo?${params.toString()}`,
      tasksDoc
    );
    return response.data;
  } catch (error) {
    console.error(
      'Error al cambiar el estado de la tarea:',
      error.response?.data || error.message
    );
    throw error;
  }
};

export const changeTaskStatus = async (
  taskId,
  statusId,
  updaterId,
  tasksDoc = {}
) => {
  try {
    const params = new URLSearchParams();
    params.append('taskId', taskId);
    params.append('statusId', statusId);
    params.append('updaterId', updaterId);

    const response = await api.put(
      `${API_BASE_URL}/Task/ChangeStatus?${params.toString()}`,
      tasksDoc // Enviamos el cuerpo (body)
    );
    return response.data;
  } catch (error) {
    console.error(
      'Error al cambiar el estado de la tarea:',
      error.response?.data || error.message
    );
    throw error;
  }
};

export const searchCases = async ({ groupId, clientId, search }) => {
  try {
    const params = {
      groupId,
      clientId: clientId || '',
      search: search || '',
    };

    const resp = await api.get('/Task/GetCasesByParameters', { params });
    return resp.data.map((c) => ({
      backendId: c.caseId,
      label: `${c.caseCode} — ${c.caseTitle}`,
      value: c.caseId,
    }));
  } catch (err) {
    console.error('Error buscando causas:', err);
    return [];
  }
};

export const addCommentWithAttachment = async (
  taskId,
  content,
  creatorId,
  file
) => {
  try {
    let fileBase64 = null;
    let fileName = null;
    let fileType = null;
    let fileSize = null;

    // 1. Si hay un archivo, conviértelo a Base64
    if (file) {
      fileBase64 = await toBase64(file);
      fileName = file.name;
      fileType = file.type;
      fileSize = file.size;
    }

    // 2. Este es el DTO 'CreateComment' que tu backend espera
    // (Coincide con tu DTO en C#)
    const commentData = {
      TipoCometario: 1, // 1 = Tarea
      CommentableId: taskId,
      CreatorId: creatorId,
      Content: content || null,
      File: fileBase64, // El string Base64
      FileName: fileName,
      FileType: fileType,
      FileSize: fileSize ? file.size : null,
    };

    // 3. Llama a tu endpoint [Route("Add")] que SÍ funciona
    const response = await api.post(
      `${API_BASE_URL}/Comment/Add`, // <<< ¡Tu endpoint [FromBody]!
      commentData, // <<< Envía el JSON
      { headers: { 'Content-Type': 'application/json' } }
    );

    return response.data;
  } catch (error) {
    console.error(
      'Error al agregar comentario:',
      error.response?.data || error.message
    );
    throw error;
  }
};

const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64String = reader.result.split(',')[1];
      resolve(base64String);
    };
    reader.onerror = (error) => reject(error);
  });

export const fetchTaskTemplatesByService = async (
  serviceId,
  taskConfiguratorId = null
) => {
  try {
    let url = `/Task/Templates/by-service/${serviceId}`;

    if (taskConfiguratorId) {
      url += `?taskConfiguratorId=${taskConfiguratorId}`;
    }

    const res = await api.get(url);

    return res.data.map((t) => ({
      label: t.name,
      value: t.id,
      html: t.content,
      extraFields: t.extraFields || [],
    }));
  } catch (error) {
    console.error('Error fetching templates:', error);
    throw error;
  }
};

export const assignDocumentToTask = async ({
  documentConfigurationId,
  taskId,
  userId,
  fileUrl,
  createdAt,
}) => {
  try {
    const payload = {
      documentConfigurationId,
      taskId,
      userId,
      fileUrl,
      createdAt: createdAt || new Date().toISOString(),
    };

    const response = await api.post(
      `${API_BASE_URL}/DocumentSend/assign-document-to-task`,
      payload
    );

    return response.data;
  } catch (error) {
    console.error(
      'Error assigning document to task:',
      error.response?.data || error.message
    );
    throw error;
  }
};

export const fetchTaskHtml = async (taskId) => {
  if (!taskId || taskId <= 0) {
    console.error('fetchTaskHtml requiere un taskId válido');
    throw new Error('TaskId must be greater than zero.');
  }

  try {
    const response = await api.get(`${API_BASE_URL}/Task/${taskId}/html`);
    return response.data;
  } catch (error) {
    console.error(
      `Error fetching HTML for taskId=${taskId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

export const updateTaskHtml = async (taskId, html) => {
  if (!taskId || taskId <= 0) {
    console.error('updateTaskHtml requiere un taskId válido');
    throw new Error('TaskId must be greater than zero.');
  }

  try {
    const body = { html };

    const response = await api.put(`${API_BASE_URL}/Task/${taskId}/html`, body);

    return response.data;
  } catch (error) {
    console.error(
      `Error updating HTML for taskId=${taskId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

export const publishTaskDocument = async (taskId) => {
  if (!taskId || taskId <= 0) {
    console.error('publishTaskDocument requiere un taskId válido');
    throw new Error('TaskId must be greater than zero.');
  }

  try {
    const response = await api.post(`${API_BASE_URL}/Task/${taskId}/publish`);

    return response.data;
  } catch (error) {
    console.error(
      `Error publishing document for taskId=${taskId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

export async function removeUserFromTask(taskId, userId, updaterId) {
  try {
    const payload = {
      id: taskId,
      userId,
      updaterId,
    };

    const response = await api.put('TaskUserSaas/RemoveUser', payload, {
      headers: { 'Content-Type': 'application/json' },
    });

    return response.data;
  } catch (error) {
    console.error('updateCase failed:', error);
    console.error('Error details:', error.response?.data);
    throw error;
  }
}

export async function exportTasksExcel(queryParams, signal) {
  try {
    const params = {
      FiltroGlobal: queryParams.filtroGlobal || null,
      StatusId: queryParams.statusId ?? null,
      CasoId: queryParams.casoId ?? null,
      UsuarioAsignadoId: queryParams.usuarioAsignadoId ?? null,
      FechaDesde: queryParams.fechaDesde || null,
      FechaHasta: queryParams.fechaHasta || null,
      FechaVencimiento: queryParams.fechaVencimiento || null,
      FechaVencimientoDesde: queryParams.fechaVencimientoDesde || null,
      FechaVencimientoHasta: queryParams.fechaVencimientoHasta || null,
      PageNumber: 1,
      PageSize: 10000,
      SortBy: queryParams.sortBy || 'fechaVencimiento',
      SortDirection: queryParams.sortDirection || 'DESC',
      CourtId: queryParams.courtId ?? null,
      BusinessAreaIds: queryParams.groups ?? null,
      PriorityId: queryParams.priorityId ?? null,
      HasDocument: queryParams.hasDocument ?? null,
      HasComments: queryParams.hasComments ?? null,
    };

    const response = await api.get('/Task/ExportExcel', {
      params,
      responseType: 'blob',
      signal,
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: 'repeat' }),
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `Tareas_${new Date().toISOString().split('T')[0]}.xlsx`
    );

    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error('Error descargando Excel:', error);
    throw error;
  }
}

export async function downloadFile({
  tipoComentario,
  commentableId,
  urlAttachment = null,
  nameAttachment = null,
}) {
  try {
    const response = await api.post(
      `${API_BASE_URL}/Comment/DowloadFile`,
      {
        TipoComentario: tipoComentario,
        CommentableId: commentableId,
        urlAttachment: urlAttachment,
        nameAttachment: nameAttachment,
      },
      {
        responseType: 'blob',
      }
    );

    const contentDisposition = response.headers['content-disposition'] || '';

    let fileName = nameAttachment ?? 'archivo';
    const match = contentDisposition.match(/filename="?(.+)"?/);
    if (match?.[1]) {
      fileName = decodeURIComponent(match[1]);
    }

    const blob = new Blob([response.data], {
      type: response.headers['content-type'],
    });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error al descargar archivo:', error);
    throw error;
  }
}

export async function getFilePreviewFile(params) {
  try {
    const response = await api.post(
      `${API_BASE_URL}/Comment/DowloadFile`,
      {
        TipoComentario: params.tipoComentario,
        CommentableId: params.commentableId,
        urlAttachment: params.urlAttachment,
        nameAttachment: params.nameAttachment,
      },
      {
        responseType: 'blob',
      }
    );

    // axios with responseType: 'blob' already returns a Blob
    const blob = response.data;
    const blobUrl = window.URL.createObjectURL(blob);

    return blobUrl;
  } catch (error) {
    console.error('Error al obtener vista previa del archivo:', error);
    throw error;
  }
}
